// @flow

import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import queryString from 'query-string';
import axios from 'axios';
import CircularProgress from '@mui/material/CircularProgress';
import dayjs from 'dayjs';
import fileDownload from 'js-file-download';
import CDAViewer from '@mdp/cda-viewer/dist/App';
import {
  useLazyGetLatestPatientEcdaQuery,
  useEmbedExportCDAAsPDFMutation,
  useLazyGetEmbedExportCDAAsPDFQuery
} from '../api/api';
import { EmbedExportCDAAsPDF } from '../types/Hcc';
import { showToastMsg } from '../features/toast-message-slice';

interface EhrRecord {
  recordXml: string;
  dataAsOf: string | undefined;
}

const ViewerAdapter = (): JSX.Element | null => {
  const [ehrRecord, setEhrRecord] = useState<EhrRecord | null>(null);
  const [patientId, setPatientId] = useState('');
  const [queryId, setQueryId] = useState('');
  const [cdaType, setCdaType] = useState<string | undefined>('');
  const [exportCDAAsPDFLoading, setExportCDAAsPDFLoading] = useState({
    1: false,
    2: false,
    3: false,
    4: false
  });
  const location = useLocation();
  const params = useParams();
  const dispatch = useDispatch();
  const [getLatestPatientEcdaQuery] = useLazyGetLatestPatientEcdaQuery();
  const [embedExportCDAAsPDFMutation] = useEmbedExportCDAAsPDFMutation();
  const [getEmbedExportCDAAsPDFQuery] = useLazyGetEmbedExportCDAAsPDFQuery();

  const getLatestPatientEcda = useCallback(
    async (patientId: string) => {
      const params = queryString.parse(location.search);

      const searchStr = new URLSearchParams(
        params as Record<string, string>
      ).toString();

      try {
        const res = await getLatestPatientEcdaQuery({
          patientId: patientId,
          queryString: searchStr
        });

        const { data } = res;
        if (!data) return;
        setPatientId(data.patientId);
        const signedUrl = data?.file_url;
        const xmlResponse = await axios.get(signedUrl);
        const dataAsOf = data.queryCreatedDt;
        let dataAsOfFormatted;

        if (dataAsOf) {
          dataAsOfFormatted = dayjs(dataAsOf).format('YYYY/MM/DD');
        }

        setQueryId(data.queryId || '');
        setCdaType(data.cdaType);

        if (typeof xmlResponse.data !== 'undefined') {
          setEhrRecord({
            recordXml: xmlResponse.data,
            dataAsOf: dataAsOfFormatted
          });
        }
      } catch (err) {
        console.log('[getLatestPatientEcda] err: ', err);
      }
    },
    [location.search, getLatestPatientEcdaQuery]
  );

  const tryGetExportedPDF = async (
    requestId: string,
    retries: number
  ): Promise<EmbedExportCDAAsPDF | null> => {
    await new Promise((resolve) => setTimeout(resolve, 10 * 1000)); // wait 10 seconds

    try {
      const res = await getEmbedExportCDAAsPDFQuery(requestId);
      const { data } = res;
      if (!data) return null;
      const { url: signedUrl } = data;

      if (signedUrl) {
        return data;
      } else if (!signedUrl && retries > 0) {
        return tryGetExportedPDF(requestId, retries - 1);
      } else {
        throw new Error('Max number of attempt reached');
      }
    } catch (error) {
      console.log('[tryGetExportedPDF] error: ', error);
      return null;
    }
  };

  const exportCDAAsPDF = async (config = {}) => {
    try {
      const searchParams = queryString.parse(location.search);
      delete searchParams.access_token;
      delete searchParams.user_email;
      searchParams.q = queryId !== 'undefined' ? queryId : '';
      searchParams.p = params.patientId ? params.patientId : '';
      const searchParamsString = new URLSearchParams(
        searchParams as Record<string, string>
      ).toString();

      await embedExportCDAAsPDFMutation({
        payload: {
          ...config,
          type: cdaType
        },
        queryStr: searchParamsString
      })
        .unwrap()
        .then(async (response: any) => {
          if (response) {
            dispatch(
              showToastMsg({
                open: true,
                message:
                  'Exporting PDF. This can take up to 5 minutes. File will download automatically when ready. Do not exit this page.',
                level: 'success',
                duration: 50000
              })
            );
            const exportedPDFResponse = await tryGetExportedPDF(
              response.requestId,
              30
            );

            if (exportedPDFResponse) {
              const {
                url: signedUrl,
                patientId,
                patientFirstName,
                patientLastName
              } = exportedPDFResponse;
              if (!signedUrl) throw new Error('No signed URL returned');
              const signedUrlResponse = await axios.get(signedUrl, {
                responseType: 'blob'
              });
              const blob = signedUrlResponse.data;
              const fileName = `${patientLastName}_${patientFirstName}_${patientId}.pdf`;
              fileDownload(blob, fileName);
            }
          } else {
            throw new Error(response);
          }
        })
        .catch((error: any) => {
          dispatch(
            showToastMsg({
              open: true,
              message:
                error?.response?.body ??
                error.message ??
                'Something went wrong',
              level: 'error',
              duration: 5000
            })
          );
        });
    } catch (error: any) {
      dispatch(
        showToastMsg({
          open: true,
          message:
            error?.response?.body ?? error.message ?? 'Something went wrong',
          level: 'error',
          duration: 5000
        })
      );
    }
  };

  const getPatientAttachment = async () => {
    // Not implemented in Embed
    // const response = await axios.get(url);
    // if (response) {
    //   const parsedResponse = JSON.parse(response.data.body);
    //   const signedUrl = parsedResponse.data;
    //   console.log('signedUrl: ', signedUrl);
    //   return signedUrl;
    // }
  };

  useEffect(() => {
    const { patientId } = params;

    if (patientId) {
      getLatestPatientEcda(patientId);
    }
  }, [location.search, params, getLatestPatientEcda]);

  return (
    <>
      <title>eCDA | {patientId}</title>
      <>
        {ehrRecord ? (
          <CDAViewer
            ehrRecord={ehrRecord}
            getPatientAttachment={getPatientAttachment}
            exportCDAAsPDF={exportCDAAsPDF}
            exportCDAAsPDFLoading={exportCDAAsPDFLoading}
            setExportCDAAsPDFLoading={setExportCDAAsPDFLoading}
          />
        ) : (
          <div className="flex items-center justify-center h-screen">
            <div
              style={{
                display: 'block',
                textAlign: 'center'
              }}
            >
              Requesting patient record, please wait.
              <br />
              <br />
              <CircularProgress />
            </div>
          </div>
        )}
      </>
    </>
  );
};

export default ViewerAdapter;
